import React, { useState } from 'react';
import { CompleteKundali, Language, PlanetName } from '../types/kundali';
import {
  analyzeVedicGemology,
  EvaluatedGemstone,
  VedicGemologyAnalysis,
  MASTER_GEMSTONES
} from '../utils/vedicGemologyEngine';
import {
  Sparkles,
  Shield,
  Award,
  AlertTriangle,
  Clock,
  Compass,
  CheckCircle2,
  XCircle,
  Gem,
  Info,
  ChevronRight,
  Flame,
  Layers,
  Calendar,
  Check,
  Zap,
  HelpCircle,
  BookOpen
} from 'lucide-react';

interface VedicGemologyPerspectivesViewProps {
  kundali: CompleteKundali;
  lang?: Language;
  onSelectGemstoneForMuhurta?: (planet: PlanetName) => void;
}

export const VedicGemologyPerspectivesView: React.FC<VedicGemologyPerspectivesViewProps> = ({
  kundali,
  lang = 'en',
  onSelectGemstoneForMuhurta
}) => {
  const analysis: VedicGemologyAnalysis = React.useMemo(() => {
    return analyzeVedicGemology(kundali);
  }, [kundali]);

  const [activePerspective, setActivePerspective] = useState<
    'TRIKONA' | 'KENDRA' | 'DASHA' | 'JAIMINI' | 'NISHEDHA'
  >('TRIKONA');

  const [inspectedGem, setInspectedGem] = useState<EvaluatedGemstone | null>(() => {
    return analysis.trikonaTrinity.lifeStone;
  });

  const getScoreBadgeColor = (score: number) => {
    if (score >= 80) return 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30';
    if (score >= 65) return 'bg-teal-500/10 text-teal-700 border-teal-500/30';
    if (score >= 45) return 'bg-amber-500/10 text-amber-700 border-amber-500/30';
    return 'bg-rose-500/10 text-rose-700 border-rose-500/30';
  };

  const renderGemCard = (gem: EvaluatedGemstone, badgeTitle?: string) => {
    const isSelected = inspectedGem?.planet === gem.planet;
    const scoreColor = getScoreBadgeColor(gem.suitabilityScore);

    return (
      <div
        key={`${gem.planet}-${badgeTitle || gem.roleTitleEn}`}
        role="button"
        tabIndex={0}
        aria-label={`${gem.gemstone.primaryStone} (${gem.roleTitleEn}), suitability score ${gem.suitabilityScore} out of 100`}
        onClick={() => setInspectedGem(gem)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setInspectedGem(gem);
          }
        }}
        className={`group relative rounded-2xl p-5 border transition-all cursor-pointer flex flex-col justify-between shadow-2xs focus:outline-hidden focus:ring-2 focus:ring-astro-primary ${
          isSelected
            ? 'border-astro-primary bg-astro-card ring-2 ring-astro-primary/20 shadow-md'
            : 'border-astro-border bg-astro-bg hover:border-astro-primary/40 hover:bg-astro-card/80'
        }`}
      >
        <div>
          {/* Top Row: Category Pill & Score */}
          <div className="flex items-center justify-between gap-2 mb-3">
            <span className="text-[10px] font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider bg-astro-primary/10 text-astro-primary border border-astro-primary/20 truncate max-w-[200px]">
              {badgeTitle || gem.categoryBadge}
            </span>
            <div className={`px-2 py-0.5 rounded-md text-[11px] font-extrabold border font-mono ${scoreColor}`}>
              {gem.suitabilityScore}/100
            </div>
          </div>

          {/* Gemstone Title & Planet */}
          <div className="flex items-start gap-3.5 mb-2">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center text-white shrink-0 shadow-xs"
              style={{ backgroundColor: gem.gemstone.hexColor }}
            >
              <Gem className="w-5 h-5 drop-shadow" />
            </div>
            <div>
              <h4 className="font-bold text-astro-text font-serif text-base leading-tight group-hover:text-astro-primary transition-colors">
                {gem.gemstone.primaryStone}
              </h4>
              <p className="text-xs text-astro-secondary-text font-medium">
                {gem.roleTitleEn} • <span>{gem.gemstone.nameHi}</span>
              </p>
            </div>
          </div>

          {/* Quick specs chips */}
          <div className="grid grid-cols-2 gap-1.5 my-3 text-[11px]">
            <div className="bg-astro-card border border-astro-border/60 px-2.5 py-1 rounded-lg text-astro-text">
              <span className="text-[10px] text-astro-secondary-text block">Finger:</span>
              <strong className="truncate block">{gem.gemstone.finger.split('(')[0]}</strong>
            </div>
            <div className="bg-astro-card border border-astro-border/60 px-2.5 py-1 rounded-lg text-astro-text">
              <span className="text-[10px] text-astro-secondary-text block">Metal:</span>
              <strong className="truncate block">{gem.gemstone.metal.split(',')[0]}</strong>
            </div>
          </div>

          {/* Tara Bala & Ashtakavarga Indicator */}
          <div className="flex flex-wrap items-center gap-1.5 text-[10px] font-medium pt-1">
            <span
              className={`px-2 py-0.5 rounded border flex items-center gap-1 ${
                gem.audits.tara.isSafe
                  ? 'bg-emerald-500/10 text-emerald-700 border-emerald-500/20'
                  : 'bg-rose-500/10 text-rose-700 border-rose-500/20'
              }`}
            >
              {gem.audits.tara.isSafe ? <Check className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
              {gem.audits.tara.nameEn} Tara
            </span>
            <span className="bg-[#F1ECE1] text-astro-text px-2 py-0.5 rounded border border-astro-border font-mono">
              SAV: {gem.audits.savBindus} (H{gem.audits.housePlaced})
            </span>
            <span className="bg-[#F1ECE1] text-astro-text px-2 py-0.5 rounded border border-astro-border font-mono">
              BAV: {gem.audits.bavBindus}
            </span>
          </div>
        </div>

        {/* Action strip */}
        <div className="pt-3 mt-3 border-t border-astro-border/50 flex items-center justify-between text-xs">
          <span className="text-astro-secondary-text text-[11px]">
            {gem.isContraindicated ? (
              <span className="text-rose-600 font-bold flex items-center gap-1">
                <XCircle className="w-3.5 h-3.5" /> High Risk
              </span>
            ) : gem.isRecommended ? (
              <span className="text-emerald-700 font-bold flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5" /> Prescribed
              </span>
            ) : (
              <span className="text-amber-700 font-medium">Trial Advised</span>
            )}
          </span>
          <span className={`text-[11px] font-bold flex items-center gap-0.5 ${isSelected ? 'text-astro-primary' : 'text-astro-secondary-text'}`}>
            {isSelected ? 'Viewing Specs' : 'Inspect'} <ChevronRight className="w-3 h-3" />
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Perspective Switcher Navigation Tabs */}
      <div className="bg-astro-bg border border-astro-border rounded-2xl p-2 shadow-2xs">
        <div className="flex items-center gap-1.5 overflow-x-auto text-xs font-semibold scrollbar-thin pb-1 sm:pb-0">
          <button
            onClick={() => {
              setActivePerspective('TRIKONA');
              setInspectedGem(analysis.trikonaTrinity.lifeStone);
            }}
            className={`px-3.5 py-2.5 rounded-xl transition-all flex items-center gap-2 shrink-0 cursor-pointer ${
              activePerspective === 'TRIKONA'
                ? 'bg-astro-primary text-white shadow-xs'
                : 'text-astro-secondary-text hover:text-astro-text hover:bg-astro-card'
            }`}
          >
            <Sparkles className="w-4 h-4 text-amber-300" />
            <span>Trikona Trinity (1, 5, 9)</span>
          </button>

          <button
            onClick={() => {
              setActivePerspective('KENDRA');
              setInspectedGem(analysis.kendraYogakaraka.yogakarakaStone || analysis.kendraYogakaraka.sukhaStone);
            }}
            className={`px-3.5 py-2.5 rounded-xl transition-all flex items-center gap-2 shrink-0 cursor-pointer ${
              activePerspective === 'KENDRA'
                ? 'bg-astro-primary text-white shadow-xs'
                : 'text-astro-secondary-text hover:text-astro-text hover:bg-astro-card'
            }`}
          >
            <Layers className="w-4 h-4 text-teal-300" />
            <span>Kendra & Yogakaraka (4, 10)</span>
          </button>

          <button
            onClick={() => {
              setActivePerspective('DASHA');
              setInspectedGem(analysis.dashaAdvisor.activeMahadasha.evaluatedGem);
            }}
            className={`px-3.5 py-2.5 rounded-xl transition-all flex items-center gap-2 shrink-0 cursor-pointer ${
              activePerspective === 'DASHA'
                ? 'bg-astro-primary text-white shadow-xs'
                : 'text-astro-secondary-text hover:text-astro-text hover:bg-astro-card'
            }`}
          >
            <Clock className="w-4 h-4 text-sky-300" />
            <span>Temporal Dasha Advisor</span>
          </button>

          <button
            onClick={() => {
              setActivePerspective('JAIMINI');
              setInspectedGem(analysis.jaiminiKarakas.atmakaraka.evaluatedGem);
            }}
            className={`px-3.5 py-2.5 rounded-xl transition-all flex items-center gap-2 shrink-0 cursor-pointer ${
              activePerspective === 'JAIMINI'
                ? 'bg-astro-primary text-white shadow-xs'
                : 'text-astro-secondary-text hover:text-astro-text hover:bg-astro-card'
            }`}
          >
            <Compass className="w-4 h-4 text-purple-300" />
            <span>Jaimini Chara Karakas</span>
          </button>

          <button
            onClick={() => {
              setActivePerspective('NISHEDHA');
              if (analysis.contraindicatedGems.length > 0) {
                setInspectedGem(analysis.contraindicatedGems[0]);
              }
            }}
            className={`px-3.5 py-2.5 rounded-xl transition-all flex items-center gap-2 shrink-0 cursor-pointer ${
              activePerspective === 'NISHEDHA'
                ? 'bg-rose-700 text-white shadow-xs'
                : 'text-rose-700 hover:text-rose-800 hover:bg-rose-50'
            }`}
          >
            <AlertTriangle className="w-4 h-4 text-amber-300" />
            <span>Contraindications (Nishedha Ratna)</span>
            {analysis.contraindicatedGems.length > 0 && (
              <span className="px-1.5 py-0.2 bg-white/20 text-white rounded-full text-[10px] font-mono">
                {analysis.contraindicatedGems.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Perspective Narrative Banner */}
      <div className="bg-astro-card border border-astro-border rounded-2xl p-5 shadow-2xs">
        {activePerspective === 'TRIKONA' && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-astro-primary font-bold text-xs uppercase tracking-wider">
              <Sparkles className="w-4 h-4" /> The Classical Trikona Doctrine (Trikona Siddhanta)
            </div>
            <h3 className="font-serif font-bold text-xl text-astro-text">
              The Lifelong Trinity: 1st, 5th, & 9th House Lords
            </h3>
            <p className="text-xs text-astro-secondary-text leading-relaxed">
              As per Maharishi Parashara, the Trikona houses (1st, 5th, 9th) are primary functional benefics (*Dharma Sthanas*). However, each Trikona lord must still be examined for secondary Dusthana lordship (6th, 8th, or 12th house rulership), combustion, planetary war, and Maraka influence before prescription. When confirmed clean, strengthening the Dharma lords provides foundational resilience and fortune.
            </p>
          </div>
        )}

        {activePerspective === 'KENDRA' && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-astro-primary font-bold text-xs uppercase tracking-wider">
              <Layers className="w-4 h-4" /> The Kendradhipati & Yogakaraka School (Kendradhipati Vichara)
            </div>
            <h3 className="font-serif font-bold text-xl text-astro-text">
              Pillars of Worldly Power: 4th & 10th House Lords & Yogakaraka
            </h3>
            <p className="text-xs text-astro-secondary-text leading-relaxed">
              While Trikonas govern internal grace and intellect, the Kendras (Vishnu Sthanas) govern tangible worldly manifestation: the <strong>4th house (Sukha)</strong> rules mental tranquility, maternal blessings, vehicles, and real estate, while the <strong>10th house (Karma)</strong> rules career authority, governance, and public status. When a single planet rules both a Kendra and Trikona, it becomes a <strong>Yogakaraka</strong>—the single most potent gemstone you can ever wear!
            </p>
          </div>
        )}

        {activePerspective === 'DASHA' && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sky-700 font-bold text-xs uppercase tracking-wider">
              <Clock className="w-4 h-4" /> The Dynamic Vimshottari Dasha Framework (Dasha Kalina Ratna)
            </div>
            <h3 className="font-serif font-bold text-xl text-astro-text">
              Time-Bound Planetary Activation & Safe Wearing Windows
            </h3>
            <p className="text-xs text-astro-secondary-text leading-relaxed">
              During an active Vimshottari Mahadasha, the ruling planet takes direct control of your karmic unfolding. However, <strong>Anukul Vad</strong> dictates that you only wear the gemstone of the Dasha lord if it is a functional benefic for your chart. If your Dasha lord rules a Dusthana (6, 8, 12) or occupies Vadha Tara, wearing its gem will amplify suffering; in such cases, prayer and charity are recommended instead.
            </p>
          </div>
        )}

        {activePerspective === 'JAIMINI' && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-purple-700 font-bold text-xs uppercase tracking-wider">
              <Compass className="w-4 h-4" /> Jaimini Chara Karakas System (Jaimini Chara Karakas)
            </div>
            <h3 className="font-serif font-bold text-xl text-astro-text">
              Atmakaraka (Soul’s Evolution) & Amatyakaraka (Career Mastery)
            </h3>
            <p className="text-xs text-astro-secondary-text leading-relaxed">
              In Maharishi Jaimini's system, planets are ranked by their degrees. The <strong>Atmakaraka (AK)</strong> represents your soul’s destiny, inner awakening, and karmic purpose. The <strong>Amatyakaraka (AmK)</strong> represents the King’s Prime Minister, determining professional breakthrough, status, intellect, and administrative acumen.
            </p>
          </div>
        )}

        {activePerspective === 'NISHEDHA' && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-rose-700 font-bold text-xs uppercase tracking-wider">
              <AlertTriangle className="w-4 h-4" /> Nishedha Ratna • Prohibited Gemstones (Nishedha Ratna)
            </div>
            <h3 className="font-serif font-bold text-xl text-rose-800">
              Strictly Inauspicious & Hazardous Stones for {kundali.houses[0]?.sign} Lagna
            </h3>
            <p className="text-xs text-astro-secondary-text leading-relaxed">
              [PROJECT_INTERPRETATION] Under this project's Anukul-vada policy, gemstones are withheld for functional malefics and lords of Dusthana houses (6th, 8th, 12th; lordship per BPHS Ch. 34), and for planets in unfavorable Taras (Vadha, Pratyak, Vipat; Navatara per Muhurta Chintamani 1.34). Classical texts describe gemological purity (Brihat Samhita) and planet–gemstone associations (Phaladeepika 2.21); withholding natal gemstones on these grounds is an interpretive remedial convention, not a direct scriptural prohibition. Gentle Upayas such as Mantra, Daana, and Seva remain available.
            </p>
          </div>
        )}
      </div>

      {/* Main Grid: Recommended Cards for Active Perspective */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {activePerspective === 'TRIKONA' && (
          <>
            {renderGemCard(analysis.trikonaTrinity.lifeStone, '🌿 Jeevan Ratna (1st House)')}
            {renderGemCard(analysis.trikonaTrinity.punyaStone, '💡 Punya Ratna (5th House)')}
            {renderGemCard(analysis.trikonaTrinity.bhagyaStone, '🌟 Bhagya Ratna (9th House)')}
          </>
        )}

        {activePerspective === 'KENDRA' && (
          <>
            {analysis.kendraYogakaraka.yogakarakaStone && (
              renderGemCard(analysis.kendraYogakaraka.yogakarakaStone, '👑 Supreme Yogakaraka')
            )}
            {renderGemCard(analysis.kendraYogakaraka.sukhaStone, '🏡 Sukha Ratna (4th House)')}
            {renderGemCard(analysis.kendraYogakaraka.karmaStone, '🏛️ Karma / Career Ratna (10th House)')}
          </>
        )}

        {activePerspective === 'DASHA' && (
          <>
            {renderGemCard(analysis.dashaAdvisor.activeMahadasha.evaluatedGem, `Active Mahadasha (${analysis.dashaAdvisor.activeMahadasha.planet})`)}
            {analysis.dashaAdvisor.activeAntardasha &&
              renderGemCard(analysis.dashaAdvisor.activeAntardasha.evaluatedGem, `Active Antardasha (${analysis.dashaAdvisor.activeAntardasha.planet})`)
            }
            {/* Lifelong fallback */}
            {renderGemCard(analysis.trikonaTrinity.bhagyaStone, 'Permanent Anchor (9th Lord)')}
          </>
        )}

        {activePerspective === 'JAIMINI' && (
          <>
            {renderGemCard(analysis.jaiminiKarakas.atmakaraka.evaluatedGem, '🕉️ Atmakaraka (Soul Purpose)')}
            {renderGemCard(analysis.jaiminiKarakas.amatyakaraka.evaluatedGem, '💼 Amatyakaraka (Career Power)')}
            {renderGemCard(analysis.trikonaTrinity.lifeStone, 'Physical Vehicle (Lagna Lord)')}
          </>
        )}

        {activePerspective === 'NISHEDHA' && (
          <>
            {analysis.contraindicatedGems.length > 0 ? (
              analysis.contraindicatedGems.map(gem => (
                renderGemCard(gem, '🚫 Strictly Prohibited')
              ))
            ) : (
              <div className="col-span-full p-8 text-center bg-astro-card border border-astro-border rounded-2xl">
                <CheckCircle2 className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                <p className="text-sm font-bold text-astro-text">No Highly Toxic Gemstones Identified</p>
                <p className="text-xs text-astro-secondary-text">Your planetary placements do not exhibit extreme Dusthana afflictions.</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Dasha Specific Timing Callout Box if in Dasha mode */}
      {activePerspective === 'DASHA' && (
        <div className="bg-sky-500/10 border border-sky-500/20 rounded-2xl p-5 space-y-3">
          <div className="flex items-center gap-2 text-sky-800 font-bold text-xs uppercase tracking-wider">
            <Clock className="w-4 h-4" /> Dasha Wearing Protocol & Validity
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-astro-card border border-astro-border rounded-xl p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-bold text-astro-text text-sm">
                  Mahadasha: {analysis.dashaAdvisor.activeMahadasha.planet}
                </span>
                <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                  analysis.dashaAdvisor.activeMahadasha.verdict === 'HIGHLY_RECOMMENDED'
                    ? 'bg-emerald-100 text-emerald-800'
                    : analysis.dashaAdvisor.activeMahadasha.verdict === 'STRICTLY_AVOID'
                    ? 'bg-rose-100 text-rose-800'
                    : 'bg-amber-100 text-amber-800'
                }`}>
                  {analysis.dashaAdvisor.activeMahadasha.verdict.replace('_', ' ')}
                </span>
              </div>
              <p className="text-xs text-astro-secondary-text leading-relaxed">
                {analysis.dashaAdvisor.activeMahadasha.verdictExplanation}
              </p>
              <div className="text-[11px] text-astro-secondary-text font-mono pt-1">
                Window: {new Date(analysis.dashaAdvisor.activeMahadasha.startDate).toLocaleDateString()} to {new Date(analysis.dashaAdvisor.activeMahadasha.endDate).toLocaleDateString()}
              </div>
            </div>

            {analysis.dashaAdvisor.activeAntardasha && (
              <div className="bg-astro-card border border-astro-border rounded-xl p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-astro-text text-sm">
                    Antardasha: {analysis.dashaAdvisor.activeAntardasha.planet}
                  </span>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                    analysis.dashaAdvisor.activeAntardasha.verdict === 'HIGHLY_RECOMMENDED'
                      ? 'bg-emerald-100 text-emerald-800'
                      : analysis.dashaAdvisor.activeAntardasha.verdict === 'STRICTLY_AVOID'
                      ? 'bg-rose-100 text-rose-800'
                      : 'bg-amber-100 text-amber-800'
                  }`}>
                    {analysis.dashaAdvisor.activeAntardasha.verdict.replace('_', ' ')}
                  </span>
                </div>
                <p className="text-xs text-astro-secondary-text leading-relaxed">
                  {analysis.dashaAdvisor.activeAntardasha.verdictExplanation}
                </p>
                <div className="text-[11px] text-astro-secondary-text font-mono pt-1">
                  Window: {new Date(analysis.dashaAdvisor.activeAntardasha.startDate).toLocaleDateString()} to {new Date(analysis.dashaAdvisor.activeAntardasha.endDate).toLocaleDateString()}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Selected Gemstone Deep Diagnostic Inspector */}
      {inspectedGem && (
        <div className="bg-astro-card border border-astro-border rounded-2xl p-6 shadow-sm space-y-6 animate-fade-in">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-astro-border pb-4">
            <div className="flex items-center gap-4">
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center text-white shrink-0 shadow-md"
                style={{ backgroundColor: inspectedGem.gemstone.hexColor }}
              >
                <Gem className="w-8 h-8 drop-shadow" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-serif font-bold text-2xl text-astro-text">
                    {inspectedGem.gemstone.primaryStone}
                  </h3>
                  <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-astro-primary/10 text-astro-primary border border-astro-primary/20">
                    {inspectedGem.gemstone.sanskritName}
                  </span>
                </div>
                <p className="text-xs text-astro-secondary-text font-medium mt-0.5">
                  Ruling Planet: <strong>{inspectedGem.planet}</strong> ({inspectedGem.gemstone.nameHi}) • Role: {inspectedGem.roleTitleEn}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="text-right">
                <div className="text-[10px] text-astro-secondary-text uppercase font-bold">Suitability Index</div>
                <div className="text-2xl font-bold font-mono text-astro-primary">
                  {inspectedGem.suitabilityScore}<span className="text-xs text-astro-secondary-text">/100</span>
                </div>
              </div>

              {onSelectGemstoneForMuhurta && (
                <button
                  onClick={() => onSelectGemstoneForMuhurta(inspectedGem.planet)}
                  className="px-4 py-2 bg-astro-primary hover:bg-astro-primary/90 text-white rounded-xl text-xs font-bold transition-all shadow-2xs flex items-center gap-1.5 cursor-pointer"
                >
                  <Calendar className="w-4 h-4" /> Calculate Muhurta
                </button>
              )}
            </div>
          </div>

          {/* Astrological Rationale & Scriptural Evidence */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="font-bold text-sm text-astro-text flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-astro-primary" /> Astrological Rationale & Positive Indicators
              </h4>
              <ul className="space-y-2">
                {inspectedGem.reasons.map((r, i) => (
                  <li key={i} className="text-xs text-astro-text flex items-start gap-2 bg-astro-bg border border-astro-border/60 p-2.5 rounded-xl">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>

              {inspectedGem.cautions.length > 0 && (
                <div className="space-y-2 pt-2">
                  <h4 className="font-bold text-sm text-rose-700 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-rose-600" /> Cautionary Factors & Disqualifiers
                  </h4>
                  <ul className="space-y-2">
                    {inspectedGem.cautions.map((c, i) => (
                      <li key={i} className="text-xs text-rose-700 flex items-start gap-2 bg-rose-500/10 border border-rose-500/20 p-2.5 rounded-xl">
                        <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Complete Wearing Specifications */}
            <div className="space-y-4">
              <h4 className="font-bold text-sm text-astro-text flex items-center gap-2">
                <Compass className="w-4 h-4 text-astro-primary" /> Consecration & Wearing Guidelines (Dharana Vidhi)
              </h4>

              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="bg-astro-bg border border-astro-border p-3 rounded-xl space-y-1">
                  <span className="text-[10px] text-astro-secondary-text uppercase font-bold block">Prescribed Finger</span>
                  <strong className="text-astro-text font-serif text-sm block">{inspectedGem.gemstone.finger}</strong>
                  <span className="text-[11px] text-astro-secondary-text block">{inspectedGem.gemstone.fingerHi}</span>
                </div>

                <div className="bg-astro-bg border border-astro-border p-3 rounded-xl space-y-1">
                  <span className="text-[10px] text-astro-secondary-text uppercase font-bold block">Resonant Metal</span>
                  <strong className="text-astro-text font-serif text-sm block">{inspectedGem.gemstone.metal}</strong>
                  <span className="text-[11px] text-astro-secondary-text block">Gold / Silver / Panchdhatu</span>
                </div>

                <div className="bg-astro-bg border border-astro-border p-3 rounded-xl space-y-1">
                  <span className="text-[10px] text-astro-secondary-text uppercase font-bold block">Auspicious Day & Time</span>
                  <strong className="text-astro-text text-xs block">{inspectedGem.gemstone.wearingDay}</strong>
                  <span className="text-[10px] text-astro-secondary-text block">{inspectedGem.gemstone.horaTime}</span>
                </div>

                <div className="bg-astro-bg border border-astro-border p-3 rounded-xl space-y-1">
                  <span className="text-[10px] text-astro-secondary-text uppercase font-bold block">Carat / Weight Guide</span>
                  <strong className="text-astro-text text-xs block">{inspectedGem.gemstone.rattiFormula}</strong>
                  <span className="text-[10px] text-astro-secondary-text block">Touch the skin directly</span>
                </div>
              </div>

              {/* Mantra Recitation */}
              <div className="bg-astro-bg border border-astro-border p-3.5 rounded-xl space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-astro-secondary-text uppercase font-bold">Beeja Mantra (108 Chants during wearing)</span>
                  <span className="text-[10px] font-mono text-astro-primary font-bold">108 Recitations</span>
                </div>
                <div className="font-mono text-astro-primary font-bold text-sm bg-astro-card border border-astro-border/60 p-2.5 rounded-lg text-center select-all">
                  {inspectedGem.gemstone.beejaMantra}
                </div>
              </div>

              {/* Substitutes / Upratnas */}
              <div className="bg-astro-bg border border-astro-border p-3 rounded-xl space-y-1">
                <span className="text-[10px] text-astro-secondary-text uppercase font-bold block">
                  Permissible Substitutes (Upratna - For Budget or Gentle Vibration)
                </span>
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {inspectedGem.gemstone.substitutes.map((sub, idx) => (
                    <span key={idx} className="bg-astro-card border border-astro-border px-2.5 py-1 rounded-lg text-xs text-astro-text font-medium">
                      ✨ {sub}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
